const CARD_SELECTOR = '[data-top-performer-celebration]'
const INITIALIZED_ATTRIBUTE = 'data-top-performer-celebration-initialized'

export const CELEBRATION_DELAY_MS = 600
export const CELEBRATION_DURATION_MS = 2000
export const CELEBRATION_PARTICLE_COUNT = 60

const PARTICLE_COLORS = ['#F2B93B', '#FCEBB4', '#1F9F8E', '#87E9DB', '#1EB8DD']

export function hasStoredCelebration(storage, key) {
    if (!storage || !key) {
        return false
    }

    try {
        return storage.getItem(key) === 'started'
    } catch {
        return false
    }
}

export function storeCelebrationStarted(storage, key) {
    if (!storage || !key) {
        return
    }

    try {
        storage.setItem(key, 'started')
    } catch {
        // Server persistence remains authoritative when browser storage is unavailable.
    }
}

export function shouldAnimateCelebration({enabled, alreadyStored, reducedMotion}) {
    return enabled && !alreadyStored && !reducedMotion
}

function showFinalState(card) {
    card.classList.remove('top-performer-celebration-pending', 'top-performer-is-celebrating')
}

async function claimCelebration(card) {
    const component = card.closest('[wire\\:id]')
    const componentId = component?.getAttribute('wire:id')

    if (!componentId || !window.Livewire) {
        return true
    }

    try {
        return await window.Livewire.find(componentId)?.call('beginTopPerformerCelebration') === true
    } catch {
        return true
    }
}

function createParticles(originX, originY) {
    return Array.from({length: CELEBRATION_PARTICLE_COUNT}, (_, index) => ({
        color: PARTICLE_COLORS[index % PARTICLE_COLORS.length],
        height: 4 + Math.random() * 5,
        rotation: Math.random() * Math.PI,
        rotationSpeed: (Math.random() - 0.5) * 0.28,
        velocityX: (Math.random() - 0.08) * 5.5,
        velocityY: -(4.5 + Math.random() * 4.5),
        width: 3 + Math.random() * 4,
        x: originX + (Math.random() - 0.5) * 8,
        y: originY + (Math.random() - 0.5) * 8,
    }))
}

function launchCardConfetti(card) {
    const trophy = card.querySelector('[data-top-performer-trophy-badge]')

    if (!(trophy instanceof HTMLElement)) {
        return
    }

    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-hidden', 'true')
    canvas.style.inset = '0'
    canvas.style.pointerEvents = 'none'
    canvas.style.position = 'absolute'
    canvas.style.zIndex = '20'
    card.append(canvas)

    const cardRect = card.getBoundingClientRect()
    const trophyRect = trophy.getBoundingClientRect()
    const pixelRatio = Math.max(1, window.devicePixelRatio || 1)
    canvas.style.height = `${cardRect.height}px`
    canvas.style.width = `${cardRect.width}px`
    canvas.height = Math.round(cardRect.height * pixelRatio)
    canvas.width = Math.round(cardRect.width * pixelRatio)

    const context = canvas.getContext('2d')

    if (!context) {
        canvas.remove()

        return
    }

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

    const originX = trophyRect.left - cardRect.left + trophyRect.width / 2
    const originY = trophyRect.top - cardRect.top + trophyRect.height / 2
    const particles = createParticles(originX, originY)
    const startedAt = performance.now()
    let previousFrame = startedAt

    const draw = (now) => {
        const elapsed = now - startedAt
        const frameScale = Math.min(2, (now - previousFrame) / 16.67)
        previousFrame = now
        context.clearRect(0, 0, cardRect.width, cardRect.height)
        context.globalAlpha = Math.max(0, 1 - elapsed / CELEBRATION_DURATION_MS)

        particles.forEach((particle) => {
            particle.x += particle.velocityX * frameScale
            particle.y += particle.velocityY * frameScale
            particle.velocityY += 0.18 * frameScale
            particle.rotation += particle.rotationSpeed * frameScale

            context.save()
            context.translate(particle.x, particle.y)
            context.rotate(particle.rotation)
            context.fillStyle = particle.color
            context.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height)
            context.restore()
        })

        if (elapsed < CELEBRATION_DURATION_MS) {
            window.requestAnimationFrame(draw)

            return
        }

        canvas.remove()
    }

    window.requestAnimationFrame(draw)
}

function playVisualCelebration(card, reducedMotion) {
    if (card.classList.contains('top-performer-is-celebrating')) {
        return
    }

    showFinalState(card)

    if (reducedMotion) return

    card.classList.add('top-performer-is-celebrating')
    launchCardConfetti(card)

    window.setTimeout(() => card.classList.remove('top-performer-is-celebrating'), CELEBRATION_DURATION_MS + 100)
}

async function startCelebration(card, storage, storageKey, reducedMotion) {
    const claimed = await claimCelebration(card)

    if (!claimed) {
        showFinalState(card)

        return
    }

    storeCelebrationStarted(storage, storageKey)
    playVisualCelebration(card, reducedMotion)
}

function initializeCard(card) {
    if (card.hasAttribute(INITIALIZED_ATTRIBUTE)) {
        return
    }

    card.setAttribute(INITIALIZED_ATTRIBUTE, 'true')

    const enabled = card.dataset.celebrationEnabled === 'true'
    const storageKey = card.dataset.celebrationStorageKey ?? ''
    let storage = null

    try {
        storage = window.localStorage
    } catch {
        // Some privacy modes block local storage; the server-side claim still prevents replay.
    }

    const alreadyStored = hasStoredCelebration(storage, storageKey)

    if (!enabled || alreadyStored) {
        showFinalState(card)

        return
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let startTimer = null

    const trigger = () => {
        startTimer ??= window.setTimeout(
            () => startCelebration(card, storage, storageKey, reducedMotion),
            CELEBRATION_DELAY_MS,
        )
    }

    if (!('IntersectionObserver' in window)) {
        trigger()

        return
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)) {
            return
        }

        observer.disconnect()
        trigger()
    }, {threshold: [0.6]})

    observer.observe(card)
}

function initializeCards(root = document) {
    if (root.matches?.(CARD_SELECTOR)) {
        initializeCard(root)
    }

    root.querySelectorAll?.(CARD_SELECTOR).forEach(initializeCard)
}

if (typeof document !== 'undefined') {
    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return

        const replay = event.target.closest('[data-top-performer-replay]')
        const card = replay?.closest(CARD_SELECTOR)

        if (card) {
            playVisualCelebration(card, window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        }
    })

    document.addEventListener('DOMContentLoaded', () => initializeCards())
    document.addEventListener('livewire:navigated', () => initializeCards())

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    initializeCards(node)
                }
            })
        })
    }).observe(document.documentElement, {childList: true, subtree: true})
}
